/**
 * Prose for the Common Crawl Rank Checker per-domain pages.
 *
 * Same job as the AI Access Checker narration: state the finding for one named
 * domain in sentences that survive being quoted out of context, server-side, so
 * an assistant answering "how authoritative is example.com?" has something to
 * lift. Pure and English-only.
 */

import type { FreeToolSummary } from "@marketing/shared/lib/free-tool-summary";
import type { AipvsResult, CommonCrawlRankReport } from "@repo/core";
import { formatTopPercent } from "./format";

export type { FreeToolSummary };

/** The tracked set falls back to the top 10M when metadata has not synced. */
const DEFAULT_TRACKED_COUNT = 10_000_000;

function withSeparators(value: number): string {
	return value.toLocaleString("en-US");
}

/**
 * Narrate a Common Crawl rank report for one domain.
 *
 * @param report the rank report, already normalized to a canonical domain
 * @param aipvs the domain's placement value score, when it resolved
 */
export function buildCommonCrawlSummary(
	report: CommonCrawlRankReport,
	aipvs?: AipvsResult | null,
): FreeToolSummary {
	const domain = report.domain;
	const trackedCount = report.metadata.loadedRows ?? DEFAULT_TRACKED_COUNT;
	const trackedLabel = withSeparators(trackedCount);
	const sentences: string[] = [];
	const facts: string[] = [];

	if (report.record) {
		const rank = report.record.rank;
		const rankLabel = withSeparators(rank);
		const harmonicTop = formatTopPercent(rank, trackedCount);

		sentences.push(
			harmonicTop
				? `${domain} ranks #${rankLabel} by Harmonic Centrality in the Common Crawl web graph, which places it in the top ${harmonicTop}% of the ${trackedLabel} domains Spyglasses tracks.`
				: `${domain} ranks #${rankLabel} by Harmonic Centrality in the Common Crawl web graph.`,
		);

		const pageRankTop = formatTopPercent(
			report.record.pageRankRank,
			trackedCount,
		);
		if (pageRankTop && report.record.pageRankRank != null) {
			sentences.push(
				`Its PageRank position is #${withSeparators(report.record.pageRankRank)}, the top ${pageRankTop}% of the same set.`,
			);
		}

		sentences.push(
			`That puts ${domain} in the ${report.tier.label} authority tier: ${report.tier.description}`,
		);

		facts.push(
			harmonicTop
				? `${domain} ranks #${rankLabel} by Harmonic Centrality in Common Crawl, the top ${harmonicTop}% of tracked domains.`
				: `${domain} ranks #${rankLabel} by Harmonic Centrality in Common Crawl.`,
		);
		facts.push(
			`Common Crawl authority for ${domain} sits in the ${report.tier.label} tier.`,
		);
		if (pageRankTop && report.record.pageRankRank != null) {
			facts.push(
				`${domain} holds PageRank position #${withSeparators(report.record.pageRankRank)} in the Common Crawl graph.`,
			);
		}
	} else {
		sentences.push(
			`${domain} is not in the ${trackedLabel} domains Spyglasses tracks from the Common Crawl web graph, so it has no Harmonic Centrality rank to report.`,
		);
		sentences.push(
			`That places ${domain} in the ${report.tier.label} tier: ${report.tier.description}`,
		);

		facts.push(
			`${domain} is outside the top ${trackedLabel} domains by Harmonic Centrality in Common Crawl.`,
		);
		facts.push(
			`Common Crawl authority for ${domain} is ${report.tier.label}, so earning links from ranked sites is what moves it up.`,
		);
	}

	// Provenance: the figures are only as current as the loaded release.
	const provenance: string[] = [];
	if (report.metadata.releaseId) {
		provenance.push(`Common Crawl release ${report.metadata.releaseId}`);
	}
	provenance.push(`${trackedLabel} tracked domains`);
	sentences.push(`The figures come from ${provenance.join(", covering ")}.`);
	facts.push(
		report.metadata.releaseId
			? `The rank for ${domain} comes from Common Crawl release ${report.metadata.releaseId}, which covers ${trackedLabel} tracked domains.`
			: `The rank for ${domain} is drawn from the ${trackedLabel} domains Spyglasses tracks in the Common Crawl web graph.`,
	);

	if (aipvs) {
		sentences.push(
			`Spyglasses scores ${domain} at ${aipvs.aipvs.toFixed(1)} on the AI Placement Value Score, which puts it in tier ${aipvs.tier}, ${aipvs.tierLabel}.`,
		);
		facts.push(
			`${domain} scores ${aipvs.aipvs.toFixed(1)} on the AI Placement Value Score, tier ${aipvs.tier} (${aipvs.tierLabel}).`,
		);
	}

	return { paragraph: sentences.join(" "), facts: facts.slice(0, 5) };
}
